import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../config';
import UserMenu from './UserMenu';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import '../styles/tailwind.css';

interface EmailAccount {
  email: string;
  password: string;
  popServer: string;
  popPort: number;
  useSsl: boolean;
}

interface MigrationStatus {
  migrationId: string;
  email?: string;
  status: 'pending' | 'starting' | 'in_progress' | 'completed' | 'failed';
  progress: number;
  message: string;
  totalEmails?: number;
  processedEmails?: number;
  failedEmails?: number;
  createdAt?: string;
  updatedAt?: string;
  completedAt?: string;
  errorMessage?: string;
  generatedFiles?: {
    mbox?: {
      fileName: string;
      s3Key: string;
      fileSize: number;
      createdAt: string;
    };
    archive?: {
      fileName: string;
      s3Key: string;
      fileSize: number;
      createdAt: string;
    };
  };
}

interface MigrationGroup {
  migrationIds: string[];
  totalAccounts: number;
  migrations: MigrationStatus[];
  overallStatus: 'pending' | 'in_progress' | 'completed' | 'failed';
  overallProgress: number;
}

// Helper function to generate S3 download URL
const getS3DownloadUrl = (s3Key: string) => {
  const bucketName = 'dashzap-old-mailboxes-staging';
  return `https://${bucketName}.s3.amazonaws.com/${s3Key}`;
};

// Helper function to format file size
const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const EmailMigration: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string>('');

  // Form data
  const [emailAccounts, setEmailAccounts] = useState<EmailAccount[]>([
    {
      email: '',
      password: '',
      popServer: 'pop.gmail.com',
      popPort: 995,
      useSsl: true
    }
  ]);

  // Migration tracking
  const [migrationGroup, setMigrationGroup] = useState<MigrationGroup | null>(null);
  const [existingMigrations, setExistingMigrations] = useState<MigrationStatus[]>([]);

  useEffect(() => {
    // Get company ID from JWT token
    const token = localStorage.getItem('dashzap_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.companyId && payload.companyId !== 'onboarding') {
        setCompanyId(payload.companyId);
        fetchExistingMigrations(payload.companyId);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error decoding token:', error);
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    if (!companyId) return;
  }, [companyId, existingMigrations]);

  const fetchExistingMigrations = async (companyId: string) => {
    try {
      const token = localStorage.getItem('dashzap_token');
      if (!token) return;

      const response = await fetch(`${config.API_BASE_URL}/migration/list/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setExistingMigrations(data.migrations || []);
      }
    } catch (error) {
      console.error('Error fetching existing migrations:', error);
    }
  };

  const addEmailAccount = () => {
    setEmailAccounts([
      ...emailAccounts,
      {
        email: '',
        password: '',
        popServer: 'pop.gmail.com',
        popPort: 995,
        useSsl: true
      }
    ]);
  };

  const removeEmailAccount = (index: number) => {
    if (emailAccounts.length > 1) {
      setEmailAccounts(emailAccounts.filter((_, i) => i !== index));
    }
  };

  const updateEmailAccount = (index: number, field: keyof EmailAccount, value: string | number | boolean) => {
    const updated = [...emailAccounts];
    updated[index] = { ...updated[index], [field]: value };
    setEmailAccounts(updated);
  };

  const validateStep1 = () => {
    return emailAccounts.every(account =>
      account.email.trim() !== '' &&
      account.password.trim() !== '' &&
      account.popServer.trim() !== '' &&
      account.popPort > 0
    );
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const startMigration = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('dashzap_token');

      const response = await fetch(`${config.API_BASE_URL}/migration/start/${companyId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          emailAccounts: emailAccounts.map(account => ({
            email: account.email,
            password: account.password,
            popServer: account.popServer,
            popPort: account.popPort,
            useSsl: account.useSsl
          }))
        })
      });

      if (response.ok) {
        // Migration started successfully
        // Refresh existing migrations to show the new one
        await fetchExistingMigrations(companyId);

        // Reset form to initial state
        setEmailAccounts([
          {
            email: '',
            password: '',
            popServer: 'pop.gmail.com',
            popPort: 995,
            useSsl: true
          }
        ]);

        // Return to step 1
        setCurrentStep(1);

        // Clear any previous migration group
        setMigrationGroup(null);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to start migration');
      }
    } catch (error) {
      console.error('Error starting migration:', error);
      setError(error instanceof Error ? error.message : 'Failed to start migration');
    } finally {
      setLoading(false);
    }
  };

  const loadInitialStatus = (migrationIds: string[]) => {
    migrationIds.forEach(migrationId => {
      loadMigrationStatus(migrationId);
    });
  };

  const loadMigrationStatus = async (migrationId: string) => {
    const token = localStorage.getItem('dashzap_token');

    try {
      const response = await fetch(`${config.API_BASE_URL}/migration/status/${migrationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();

        setMigrationGroup(prev => {
          if (!prev) return prev;

          const updatedMigrations = prev.migrations.map(migration =>
            migration.migrationId === migrationId
              ? { ...migration, ...data, progress: data.progress || 0 }
              : migration
          );

          const totalProgress = updatedMigrations.reduce((sum, m) => sum + m.progress, 0);
          const overallProgress = Math.round(totalProgress / updatedMigrations.length);

          const completedCount = updatedMigrations.filter(m => m.status === 'completed').length;
          const failedCount = updatedMigrations.filter(m => m.status === 'failed').length;
          const inProgressCount = updatedMigrations.filter(m => m.status === 'in_progress' || m.status === 'starting').length;

          let overallStatus: 'pending' | 'in_progress' | 'completed' | 'failed';
          if (failedCount > 0) {
            overallStatus = 'failed';
          } else if (completedCount === updatedMigrations.length) {
            overallStatus = 'completed';
          } else if (inProgressCount > 0) {
            overallStatus = 'in_progress';
          } else {
            overallStatus = 'pending';
          }

          return {
            ...prev,
            migrations: updatedMigrations,
            overallProgress,
            overallStatus
          };
        });
      }
    } catch (error) {
      console.error('Error loading migration status:', error);
    }
  };

  const getProgressColor = (status?: string) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'failed': return '#ef4444';
      case 'in_progress':
      case 'starting': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const renderExistingMigrations = () => (
    <Card className="mb-8">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Existing Migrations</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => companyId && fetchExistingMigrations(companyId)}
            disabled={!companyId}
          >
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {existingMigrations.length === 0 ? (
          <div className="text-center py-8 text-gray-500 italic">
            <p>No migrations found. Start your first email migration below.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full border-collapse min-w-[700px]">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border-b-2 border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">
                    Email
                  </th>
                  <th className="border-b-2 border-gray-200 px-4 py-3 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">
                    Status
                  </th>
                  <th className="border-b-2 border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">
                    Progress
                  </th>
                  <th className="border-b-2 border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">
                    Emails
                  </th>
                  <th className="border-b-2 border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">
                    Created
                  </th>
                  <th className="border-b-2 border-gray-200 px-4 py-3 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">
                    Downloads
                  </th>
                </tr>
              </thead>
              <tbody>
                {existingMigrations.map((migration) => (
                  <tr key={migration.migrationId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 border-b border-gray-100 min-w-[200px]">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 text-sm">{migration.email}</span>
                        {migration.status === 'failed' && migration.errorMessage && (
                          <div className="relative group">
                            <span className="cursor-help text-sm">⚠️</span>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                              <div className="bg-gray-700 text-white px-3 py-2 rounded-md text-xs max-w-xs whitespace-normal shadow-lg">
                                {migration.errorMessage}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-700"></div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3 border-b border-gray-100 min-w-[100px]">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-medium capitalize whitespace-nowrap ${
                          migration.status === 'completed'
                            ? 'bg-green-100 text-green-700'
                            : migration.status === 'failed'
                            ? 'bg-red-100 text-red-700'
                            : migration.status === 'in_progress' || migration.status === 'starting'
                            ? 'bg-blue-100 text-blue-700'
                            : migration.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {migration.status.replace('_', ' ')}
                      </span>
                    </td>

                    <td className="px-4 py-3 border-b border-gray-100 min-w-[80px] text-center">
                      <div className="flex justify-center items-center">
                        <div className="relative inline-flex items-center justify-center">
                          <svg className="w-10 h-10 transform -rotate-90" viewBox="0 0 40 40">
                            <circle
                              cx="20"
                              cy="20"
                              r="16"
                              stroke="#e2e8f0"
                              strokeWidth="3"
                              fill="transparent"
                            />
                            <circle
                              cx="20"
                              cy="20"
                              r="16"
                              stroke={getProgressColor(migration.status)}
                              strokeWidth="3"
                              strokeLinecap="round"
                              fill="transparent"
                              strokeDasharray={`${2 * Math.PI * 16}`}
                              strokeDashoffset={`${2 * Math.PI * 16 * (1 - (migration.progress || 0) / 100)}`}
                              style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                            />
                          </svg>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 border-b border-gray-100 min-w-[120px] text-center">
                      {migration.totalEmails && migration.processedEmails !== undefined ? (
                        <div className="text-sm">
                          <span className="font-semibold text-gray-900">{migration.processedEmails}</span>
                          <span className="text-gray-500">/{migration.totalEmails}</span>
                          {migration.failedEmails ? (
                            <span className="text-red-600 text-xs ml-1">({migration.failedEmails} failed)</span>
                          ) : null}
                        </div>
                      ) : migration.status === 'pending' ? (
                        <span className="text-xs text-gray-500 italic">Initializing...</span>
                      ) : migration.status === 'failed' ? (
                        <span className="text-xs text-red-600 italic">Failed</span>
                      ) : (
                        <span className="text-xs text-gray-500 italic">Processing...</span>
                      )}
                    </td>

                    <td className="px-4 py-3 border-b border-gray-100 min-w-[100px] text-center text-sm text-gray-600">
                      {migration.createdAt ? new Date(migration.createdAt).toLocaleDateString() : 'N/A'}
                    </td>

                    <td className="px-4 py-3 border-b border-gray-100 min-w-[100px] text-center">
                      {migration.status === 'completed' && migration.generatedFiles ? (
                        <div className="flex gap-2 justify-center items-center">
                          {migration.generatedFiles.mbox && (
                            <a
                              href={getS3DownloadUrl(migration.generatedFiles.mbox.s3Key)}
                              download={migration.generatedFiles.mbox.fileName}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-blue-500 hover:bg-blue-600 text-white transition-all hover:-translate-y-0.5 hover:shadow-md"
                              title={`MBOX (${formatFileSize(migration.generatedFiles.mbox.fileSize)})`}
                            >
                              📧
                            </a>
                          )}
                          {migration.generatedFiles.archive && (
                            <a
                              href={getS3DownloadUrl(migration.generatedFiles.archive.s3Key)}
                              download={migration.generatedFiles.archive.fileName}
                              className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-green-500 hover:bg-green-600 text-white transition-all hover:-translate-y-0.5 hover:shadow-md"
                              title={`Archive (${formatFileSize(migration.generatedFiles.archive.fileSize)})`}
                            >
                              📦
                            </a>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Email Account Configuration</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Configure the email accounts you want to migrate. You'll need app-specific passwords for Gmail accounts.
        </p>
      </div>

      <div className="space-y-4">
        {emailAccounts.map((account, index) => (
          <Card key={index} className="bg-gray-50">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">Email Account {index + 1}</CardTitle>
                {emailAccounts.length > 1 && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => removeEmailAccount(index)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </CardHeader>

            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor={`email-${index}`}>Email Address</Label>
                  <Input
                    id={`email-${index}`}
                    type="email"
                    value={account.email}
                    onChange={(e) => updateEmailAccount(index, 'email', e.target.value)}
                    placeholder="user@example.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`password-${index}`}>App-Specific Password</Label>
                  <Input
                    id={`password-${index}`}
                    type="password"
                    value={account.password}
                    onChange={(e) => updateEmailAccount(index, 'password', e.target.value)}
                    placeholder="App-specific password"
                    required
                  />
                  <p className="text-xs text-gray-500">
                    For Gmail, generate an app-specific password in your Google Account settings
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`pop-server-${index}`}>POP3 Server</Label>
                  <Input
                    id={`pop-server-${index}`}
                    type="text"
                    value={account.popServer}
                    onChange={(e) => updateEmailAccount(index, 'popServer', e.target.value)}
                    placeholder="pop.gmail.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`pop-port-${index}`}>POP3 Port</Label>
                  <Input
                    id={`pop-port-${index}`}
                    type="number"
                    value={account.popPort}
                    onChange={(e) => updateEmailAccount(index, 'popPort', parseInt(e.target.value))}
                    placeholder="995"
                    required
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`use-ssl-${index}`}
                    checked={account.useSsl}
                    onCheckedChange={(checked: boolean) => updateEmailAccount(index, 'useSsl', !!checked)}
                  />
                  <Label htmlFor={`use-ssl-${index}`} className="cursor-pointer">
                    Use SSL/TLS
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        <Button type="button" variant="outline" onClick={addEmailAccount} className="w-full">
          Add Another Email Account
        </Button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Review Configuration</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Please review your email migration configuration before starting the backup process.
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Email Accounts to Migrate</h3>
          {emailAccounts.map((account, index) => (
            <Card key={index} className="mb-3 bg-gray-50">
              <CardContent className="pt-4">
                <div>
                  <strong className="text-gray-900 block mb-1">{account.email}</strong>
                  <div className="text-sm text-gray-600 font-mono">
                    {account.popServer}:{account.popPort} {account.useSsl ? '(SSL)' : '(No SSL)'}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-900">Migration Process</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-blue-900 text-sm space-y-2 list-disc pl-5">
              <li>All emails will be downloaded using POP3 protocol</li>
              <li>Emails will be stored securely in Amazon S3</li>
              <li>Original emails will remain in your accounts</li>
              <li>You can monitor progress in real-time</li>
              <li>Migration can take several hours depending on email volume</li>
            </ul>
          </CardContent>
        </Card>

        {error && (
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-4 text-red-600 text-sm">
              {error}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Migration in Progress</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Your email migration is {migrationGroup?.overallStatus || 'starting'}. Please keep this page open to monitor progress.
        </p>
      </div>

      {migrationGroup && (
        <div className="space-y-6">
          {/* Overall Progress */}
          <Card className="bg-gray-50">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Overall Progress ({migrationGroup.migrations.length} accounts)
              </h3>
              <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden mb-4">
                <div
                  className="h-full transition-all duration-500 ease-out rounded-full"
                  style={{
                    width: `${migrationGroup.overallProgress || 0}%`,
                    backgroundColor: getProgressColor(migrationGroup.overallStatus)
                  }}
                ></div>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-2xl font-bold text-gray-900">{migrationGroup.overallProgress || 0}%</span>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {migrationGroup.overallStatus}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Individual Account Progress */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Individual Account Progress</h3>
            {migrationGroup.migrations.map((migration) => (
              <Card key={migration.migrationId} className="mb-4">
                <CardContent className="pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <span className="font-medium text-gray-900">{migration.email}</span>
                    <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700 capitalize">
                      {migration.status}
                    </span>
                  </div>

                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
                    <div
                      className="h-full transition-all duration-500 ease-out rounded-full"
                      style={{
                        width: `${migration.progress || 0}%`,
                        backgroundColor: getProgressColor(migration.status)
                      }}
                    ></div>
                  </div>

                  <div className="flex justify-between items-center text-sm text-gray-600">
                    <span>{migration.progress || 0}%</span>
                    {migration.totalEmails && migration.processedEmails !== undefined && (
                      <span>
                        {migration.processedEmails} of {migration.totalEmails} emails
                        {migration.failedEmails ? ` (${migration.failedEmails} failed)` : ''}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {migrationGroup.overallStatus === 'completed' && (
            <div className="flex justify-center">
              <Button onClick={() => navigate('/admin-dashboard')}>
                Return to Dashboard
              </Button>
            </div>
          )}

          {migrationGroup.overallStatus === 'failed' && (
            <div className="flex gap-4 justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  setCurrentStep(1);
                  setMigrationGroup(null);
                }}
              >
                Try Again
              </Button>
              <Button onClick={() => navigate('/admin-dashboard')}>
                Return to Dashboard
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <UserMenu />

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Email Migration</h1>
          <p className="text-gray-600 mt-2">Backup your emails to secure cloud storage</p>
        </div>

        {renderExistingMigrations()}

        {/* Progress Steps */}
        <div className="flex justify-center items-center gap-8 mb-8">
          <div className={`flex flex-col items-center gap-2 transition-opacity ${currentStep >= 1 ? 'opacity-100' : 'opacity-50'}`}>
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold transition-all ${
                currentStep >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              1
            </div>
            <div className={`text-sm font-medium ${currentStep >= 1 ? 'text-gray-900' : 'text-gray-600'}`}>
              Configure
            </div>
          </div>

          <div className={`flex flex-col items-center gap-2 transition-opacity ${currentStep >= 2 ? 'opacity-100' : 'opacity-50'}`}>
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold transition-all ${
                currentStep >= 2 ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              2
            </div>
            <div className={`text-sm font-medium ${currentStep >= 2 ? 'text-gray-900' : 'text-gray-600'}`}>
              Review
            </div>
          </div>

          <div className={`flex flex-col items-center gap-2 transition-opacity ${currentStep >= 3 ? 'opacity-100' : 'opacity-50'}`}>
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-semibold transition-all ${
                currentStep >= 3 ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}
            >
              3
            </div>
            <div className={`text-sm font-medium ${currentStep >= 3 ? 'text-gray-900' : 'text-gray-600'}`}>
              Migrate
            </div>
          </div>
        </div>

        {/* Migration Content */}
        <Card className="mb-8">
          <CardContent className="pt-8 min-h-[400px]">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {currentStep < 3 && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-between items-center">
                {currentStep > 1 && (
                  <Button variant="outline" onClick={handleBack} disabled={loading}>
                    Back
                  </Button>
                )}

                {currentStep === 1 && (
                  <Button onClick={handleNext} disabled={!validateStep1()} className="ml-auto">
                    Next
                  </Button>
                )}

                {currentStep === 2 && (
                  <Button onClick={startMigration} disabled={loading} className="ml-auto">
                    {loading ? 'Starting Migration...' : 'Start Migration'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EmailMigration;
